import type { UserEntitlementState } from '../entitlements';
import type { EntitlementType } from '../master-data/types/entitlement.types';
import type { DifficultySelection } from '../master-data/types/difficulty.types';
import type { MigrationSourceState } from '../migration/types';
import type { SplashStateKey } from './splash-keys';
import type { GloryShopPurchaseCounter } from '../glory-shop/limits';
import type { ShopPurchaseCounter } from '../shop/limits';
import type { UserTimedQuestState } from '../timed-quest/types';

export interface UserCharacterState {
  characterId: number;
}

export interface UserUpgradeState {
  characterId: number;
  upgradeId: number;
  level: number;
}

export interface UserPetState {
  petId: number;
  petDocId?: string;
  level: number;
  /**
   * @deprecated 더 이상 write 하지 않음. 장착은 클라 equipped-pet.store 가 소유하고
   * 서버 복구본은 equippedPetSlots 가 단일 소스다. 잔존 데이터는 read 되지 않음.
   */
  slotNumber?: number | null;
  /** RB 펫 돌파: 현재 돌파 단계(0..PET_MAX_BREAKTHROUGH). grade5+ 만 사용. 미돌파=0/undefined. */
  breakthrough?: number;
  /** RB 펫 돌파: 미소모 중복 보유 수(합성 재료). grade5+ 중복 획득 시 +1. 미보유=0/undefined. */
  dupeStock?: number;
}

export interface EquippedPetSlot {
  petId: number;
  slotNumber: number;
}

export interface UserQuestState {
  questId: number;
  progress: number;
  isCompleted: boolean;
  isClaimed: boolean;
  completedAt: number | null;
  periodKey?: string;
}

export interface UserGuideQuestState {
  guideQuestId: number;
  progress: number;
  isCompleted: boolean;
  isClaimed: boolean;
  completedAt: number | null;
}

export interface TicketRefillState {
  refillStartTime: number | null;
}

export interface UserTicketRechargeState {
  stage: TicketRefillState;
  league: TicketRefillState;
  valhalla: TicketRefillState;
  /**
   * 폴크방 입장권 리필 타이머. 다른 셋과 달리 optional 인 이유는 이 필드가 나중에
   * 추가돼 기존 유저 state 에는 키 자체가 없기 때문이다 — 서버 applyTicketRefills 가
   * 최초 호출 때 잔량 기준으로 채워 넣는다.
   */
  folkvangr?: TicketRefillState;
  // Degen tickets intentionally have no time-based refill — see
  // PROJECT/system/design/degen-league.md §8.1 (Web3-only purchase, no free).
}

/** 방치 보상 상태 (packages/shared/src/idle-reward.ts 순수 정산 함수와 짝). */
export interface UserIdleState {
  lastClaimAt: number; // epoch ms 누적 앵커
  sapphireDay: string | null; // 마지막 Sapphire 지급 UTC 일자 "YYYY-MM-DD"
  qgDay: string | null; // 퀵게인 daily 카운터 기준일
  qgFree: number;
  qgAd: number;
  qgTx: number;
  pendingBonus: {
    id: string;
    baseAmounts: Record<string, number>; // gold/ruby/emerald 기본 수령액
    multApplied: number; // 1.0 기본, w3 tx 후 1.20
    expiresAt: number; // epoch ms
  } | null;
}

export interface UserAttendanceState {
  checkInCount: number;
  lastClaimDate: string;
  /** Tracks which week schedule the user is on (0-indexed). Incremented when a 7-day cycle completes. */
  weekIndex: number;
}

/**
 * Per-track level config for a pass (one entry per day/level).
 * Stored on UserPassState.passTrack so claim validation is server-trusted.
 */
export interface UserPassTrackLevel {
  level: number;
  rewards: Record<string, number>;
}

/**
 * Per-pass state stored in UserGlobalState.passes[productId].
 * Each pass is identified by its VX Shop productId.
 */
export interface UserPassState {
  /** When the pass was first activated (or last renewed), epoch ms */
  activatedAt: number;
  /** When the pass expires, epoch ms */
  expiresAt: number;
  /** Number of track rewards claimed so far (1-indexed, 0 = none) */
  claimedLevel: number;
  /**
   * 재구매로 스택된 사이클 수 (1 = 단일 기간). 신규 활성은 1, 활성 중 재구매마다 +1.
   * totalLevels = cycles × track.length. 레거시(미존재) 레코드는 1 로 취급(별도 backfill 로 보정).
   */
  cycles?: number;
  /** Server-stored track snapshot used for trusted claim validation. */
  passTrack?: UserPassTrackLevel[];
}

export interface UserPetPreviewSummonState {
  sessionId: string;
  configId: string;
  summonCount: number;
  /** UTC date when this preview was issued (YYYY-MM-DD). Active only on this date. */
  previewDate: string;
  /**
   * Server-derived petIds frozen at issuance. Client renders the preview
   * directly from this list — no client-side draw or seed re-roll. Matches
   * the worker's re-derivation against the issuance record.
   */
  petIds: number[];
  /**
   * Indices into `petIds` that the server flagged as golden-corrected at
   * issuance. Optional — empty/absent means no golden corrections applied.
   */
  isGoldenCorrectedIndices?: number[];
  purchased?: boolean;
}

/** Snapshot entry for an invitee that redeemed but hasn't qualified yet.
 *  Stored inside inviter's UserGlobalState.invitePendingList. */
export interface PendingInvitee {
  account: string;
  nickname: string;
  redeemedAt: number;
}

export interface UserGlobalState {
  initialized: boolean;
  selectedCharacterId: number;
  lastClearedStageId: number | null;
  // Nickname system: auto-generated on first login, changeable once
  nickname?: string;
  nicknameHashCode?: string;
  nicknameChanged?: boolean;
  characters: UserCharacterState[];
  upgrades: UserUpgradeState[];
  pets: UserPetState[];
  missions?: UserQuestState[];
  quests?: UserQuestState[];
  // ── 시간제한 퀘스트 ──
  /** 트리거 이벤트 원장. 키(`EVENT` 또는 `EVENT:param`) → 발생 시각(ms) */
  timedQuestEvents?: Record<string, number>;
  /** 시간제한 퀘스트 인스턴스. 같은 questId 는 동시에 하나만 존재한다 */
  timedQuests?: UserTimedQuestState[];
  guideQuests: UserGuideQuestState[];
  leagueTickets: never[];
  suspiciousActivityCount?: number;
  // Ban state — set by admin via banUser/unbanUser
  banned?: boolean;
  bannedAt?: number;
  bannedBy?: string;
  banReason?: string;
  // Total number of pets obtained (STANDARD + GOLDEN + confirmed PREVIEW)
  // Used for both summon level calculation and golden trigger detection
  petSummonedCount?: number;
  // Golden summon count for the guide-quest progress ("골든 소환 횟수").
  // Incremented +1 per golden 10-pull claim. NOT used for golden eligibility.
  petGoldenSummonUsedCount?: number;
  // Golden pity high-water mark: the count-value of the highest golden threshold
  // already claimed. Golden eligibility compares this against the config
  // thresholds, so it is config-independent (changing the trigger schedule never
  // locks out or grants a backlog). Absent → lazy-inits to petSummonedCount on
  // the next summon. See packages/shared/src/pet-summon.ts.
  petGoldenClaimedThroughCount?: number;
  petSlotCount?: number;
  /** RB: 퀘스트(별모으기 등)로 받은 펫 슬롯 수. 5·6번 위치 배치용 힌트. 기본 0.
   * 장착 용량 권위 아님(그건 petSlotCount). PET_SLOT_QUEST delivery 시 +1. */
  questPetSlotCount?: number;
  // Authoritative equip state: which pets are in which slots
  equippedPetSlots?: EquippedPetSlot[];
  purchaseCounts?: Record<string, number>;
  /**
   * 글로리 샵 상품별 구매 카운터. key = glory_shop_item.id.
   *
   * 기존 `purchaseCounts` 는 **자산키** 기반(업그레이드 리셋권 등)이라 의미가 달라
   * 재사용하지 않는다. 주기 리셋은 구매 시점 lazy 처리(@rb/shared/glory-shop/limits).
   */
  gloryShopPurchases?: Record<string, GloryShopPurchaseCounter>;
  /**
   * 무료/광고 수령 상품별 획득 카운터. key = free_shop_item.id.
   * 주기 리셋은 획득 시점 lazy 처리(@rb/shared/shop/limits).
   */
  freeShopClaims?: Record<string, ShopPurchaseCounter>;
  petPreviewSummon?: UserPetPreviewSummonState | null;
  attendance?: UserAttendanceState;
  eventAttendance?: UserAttendanceState;
  pwaAttendance?: UserAttendanceState;
  passes?: Record<string, UserPassState>;
  /** 패스 보유 기간 동안 활성화되는 entitlement (자체 expiresAt) */
  entitlements?: Partial<Record<EntitlementType, UserEntitlementState>>;
  ticketRecharge?: UserTicketRechargeState;
  /** 방치 보상 상태 (idle-reward.ts 순수 정산 함수와 짝). */
  idleState?: UserIdleState;
  // Valhalla: per-tier highest reached wave. Display + analytics only.
  valhallaHighestWave?: Record<number, number>;
  // Valhalla: highest tier the user has fully cleared. Linear progression —
  // tier N+1 unlocks when this >= N. Monotonic increasing.
  lastClearedValhallaTierId?: number;
  /** 현재 발할라 티어 진행도가 속한 시즌 id. 활성 시즌과 다르면 리셋 트리거.
   *  undefined = 시즌제 도입 전 유저(첫 진입 시 리셋). */
  valhallaSeasonId?: number;
  /** 발할라 소탕 일일 카운터 (UTC date 리셋) */
  valhallaSweepDaily?: { date: string; count: number };
  // Stage personal best score per stageId. NOT a ranking — just self-tracking.
  stageBestScores?: Record<number, number>;
  /**
   * RB-1847: stageId → preMasteryScore(= baseScore × clearedWaveCountMultiplier,
   * 즉 최종점수에서 마스터리 배율을 제거한 성능 성분). stageBestScores 최고점 갱신과
   * 동일 트랜잭션에서만 함께 갱신해 두 맵이 발산하지 않게 한다. 구기록은 키 부재 →
   * 예상 점수 미표시(신 스키마 기록에만 표시).
   */
  stageBestBaseScores?: Record<number, number>;
  /**
   * 보유 스킨. `level` 은 스킨 강화(v3, `UserSkinHandler.upgradeSkin`) 레벨 —
   * **미설정 = L1(기본값)** 취급(v3 부터, 구 v2 의 미설정=0 취급과 다름). 컬렉션
   * SCP 계산 시에도 미설정=1(L1) 로 읽는다.
   */
  ownedSkins?: { characterId: number; skinId: number; level?: number }[];
  /**
   * 스킨 강화(`upgradeSkin`) 멱등키 최근 처리 결과 링 버퍼. 같은
   * `idempotencyKey` 로 재호출되면 burn 을 다시 하지 않고 이 로그에서 찾은
   * 결과를 그대로 반환한다(기획 §7 — 쿠폰 이중 지급 200% 선례로 필수 지정).
   * `recentPurchases`(`PurchaseReceiptEntry`)와 동일 규약 — 최근
   * `SKIN_UPGRADE_IDEM_MAX` 건만 유지하는 ring buffer.
   */
  skinUpgradeIdemLog?: SkinUpgradeIdemEntry[];
  /**
   * 조건 언락된 스킨 목록(skin_unlock 판정 결과). **영구·역행 무관** — 한 번
   * 언락되면 진행도가 낮아져도 유지된다. 서버 재평가(evaluateSkinUnlocks 안전망
   * RPC)가 자체 상태 기준으로 append 하며, 판정 캐시(스냅샷 등)는 users 문서에
   * 저장하지 않는다(매 평가마다 재계산). `unlockedAt` 은 epoch ms(서버 시계).
   */
  skinUnlocks?: { skinId: number; unlockedAt: number }[];
  /**
   * 마지막 인게임 플레이에 사용된 캐릭터별 장착 스킨 (복구용). getSeed 시점에만
   * 서버가 덮어쓴다. 클라가 매번 동기화하지 않으며, 클라 로컬 장착 정보가 비었을
   * 때만 이 값으로 1회 init 한다. `{ [characterId]: skinId | null }`.
   */
  equippedSkins?: Record<number, number | null>;
  /** equippedSkins 가 클라에서 장착된 시각 (epoch ms, 클라 시계). LWW 비교용. */
  equippedSkinsSavedAt?: number;
  /** equippedPetSlots 가 클라에서 장착된 시각 (epoch ms, 클라 시계). LWW 비교용. */
  equippedPetSlotsSavedAt?: number;
  /**
   * 디젠리그 마지막 입장 세팅(난이도·베팅 티켓 수). degenLeague.getSeed 시점에만
   * 서버가 덮어쓴다. 릴/베팅바 기본값 복원용 — 권위값 아님(티켓 잔액·해금 게이트는
   * 입장 때 항상 재검증한다).
   *
   * equippedSkins 와 달리 LWW savedAt 을 두지 않는다: 저 필드들은 소유권이 클라에
   * 있어 양쪽이 write 하므로 충돌 판정이 필요하지만, 이 값은 서버 단독 write 이고
   * 클라는 읽기 전용이라 충돌 자체가 발생하지 않는다.
   */
  lastDegenBet?: { difficulty: import('../degen').DegenDifficulty; ticketCount: number };
  adRemovalPurchased?: boolean;
  /** Per-ticket-type daily ad claim counts, e.g. { date: "2026-03-28", counts: { stage_ticket: 2, valhalla_ticket: 1 } } */
  adTicketClaims?: { date: string; counts: Record<string, number> };
  /** Per-ticket-type daily web3 tx claim counts, mirrors adTicketClaims shape */
  txTicketClaims?: { date: string; counts: Record<string, number> };
  // Play counts — incremented on ticket consumption (stage=clear, league/valhalla=getSeed,
  // folkvangr=folkvangr.play). `folkvangr` 는 뒤늦게 추가돼 기존 유저 문서에는 없다 —
  // 읽는 쪽은 반드시 `?? 0` 으로 받는다.
  playCountsTotal?: { stage: number; league: number; valhalla: number; folkvangr?: number };
  playCountsDaily?: {
    date: string;
    stage: number;
    league: number;
    valhalla: number;
    folkvangr?: number;
  };
  playCountsWeekly?: {
    weekKey: string;
    stage: number;
    league: number;
    valhalla: number;
    folkvangr?: number;
  };
  // Pet summon counts by type (STANDARD/GOLDEN/PREVIEW)
  petSummonCountsByType?: Record<string, number>;
  petSummonCountsDaily?: { date: string; counts: Record<string, number>; total: number };
  petSummonCountsWeekly?: { weekKey: string; counts: Record<string, number>; total: number };
  // Upgrade counts (daily/weekly)
  characterUpgradeCountDaily?: { date: string; count: number };
  characterUpgradeCountWeekly?: { weekKey: string; count: number };
  petUpgradeCountDaily?: { date: string; count: number };
  petUpgradeCountWeekly?: { weekKey: string; count: number };
  // Lifetime totals (never reset)
  characterUpgradeCountTotal?: number;
  petUpgradeCountTotal?: number;
  leagueHighestScore?: number;
  /** 오버드라이브 런(배팅 ≥1) 클리어 최종점수 누적 — CUMULATIVE 퀘스트 OVERDRIVE_SCORE 조건 소스. */
  overdriveScoreTotal?: number;
  // RB-1867: 밴 시 아카이브로 옮긴 랭킹 원본 컬렉션명 목록. 언밴 시 이 목록을
  // 순회해 아카이브 → 원본으로 복구하고 clear 한다.
  rankingArchiveRefs?: string[];
  questMilestones?: import('../master-data/types/quest.types').UserQuestMilestoneState[];
  adDoubledStageIds?: number[];
  lastClaimedGuideQuestId?: number;
  // Per-season league play count — only stores the most recent season to prevent data bloat
  leagueSeasonPlayCount?: { seasonId: number; count: number };
  // Total star count across all stages (cached for quest evaluation)
  totalStarCount?: number;
  /**
   * 이 계정이 이미 지급받은 `gameConfig.initialAssets` 키 목록 (키 단위 1회 지급 원장).
   * 마스터데이터에 새 재화가 추가되면 스플래시에서 이 목록과 diff 해 그 키만 1회
   * mint 한다 — 자세한 계약은 `services/server/src/utils/initial-assets-backfill.ts`.
   * 기록이 없는 계정은 장치 도입 이전 가입자로 보고 동결 상수로 시드한다.
   */
  initialAssetsGranted?: string[];
  // ── Invite system (RB-1186) ──
  // Account creation timestamp — set once in init() for new users.
  // Existing pre-RB-1186 users have undefined → never invitee-eligible.
  createdAt?: number;
  // Inviter account this user redeemed. Permanent — set once, never changes.
  invitedBy?: string;
  // Snapshot of inviter's nickname at redeem time (display in InviteDetailsModal).
  invitedByNickname?: string;
  // True once the invite reward has been delivered for this user.
  // Idempotency flag — prevents double-grant across re-triggers.
  invitedByRewarded?: boolean;
  // Inviter-side: total successful invites (lifetime, never resets).
  inviteSuccessCount?: number;
  // Inviter-side: total Ruby rewarded across invites (lifetime cap check).
  inviteRewardedRubyTotal?: number;
  // Inviter-side: per-day cap enforcement, UTC date.
  inviteDailyCount?: { date: string; count: number };
  // Inviter-side: snapshot of invitees that redeemed but haven't qualified yet.
  // Pushed in redeemInviteCode, removed in evaluateAndDeliver on qualify.
  invitePendingList?: PendingInvitee[];
  // 계정의 게스트 여부 스냅샷. init()에서 $sender.isGuest로 write-once 저장.
  // 게스트/로그인 계정 ID는 분리돼 있어 계정당 불변 → 한 번 쓰면 갱신 불필요.
  // 레거시(필드 없던 시절)는 undefined → invite redeem 판별 시 "게스트 아님"으로 간주.
  isGuest?: boolean;
  // ── PWA 설치 보상 ──
  // PWA 설치 배너에서 인스톨을 누른 시점(클리어 요청). 미클릭이면 undefined. 1회성.
  // 설치 자체를 검증하지 않는다(다른 도메인). 클릭 = 클리어로 신뢰.
  pwaInstalledAt?: number;
  // ── 게스트 마이그레이션 (타겟 측 표식) ──
  // 이 계정이 마지막으로 게스트 마이그레이션을 받은 시각(epoch ms). 핸들러가 매
  // 마이그레이션마다 Date.now() 로 덮어씀. 게스트의 `migrated`(데이터가 빠져나가고
  // reset 됨) 와는 의미가 다른 audit 필드. 다회 누적 history 는 `migrationHistory` 가 담당.
  // (구 룰1 = 마이그레이션 영구 차단 시그널은 firstPurchasedAt+adRemovalPurchased+passes 로 이전됨)
  migrationReceivedAt?: number;
  // ── 게스트 마이그레이션 (audit 확장) ──
  // 타겟이 받은 마이그레이션 history (매 성공마다 append). 후속 추적·CS 용도.
  // migrationReceivedAt 은 "최근 1건" 단일 timestamp 로 유지하고, 다회 누적은 이 리스트가 담당.
  migrationHistory?: MigrationHistoryEntry[];
  // ── 게스트 마이그레이션 (소스 측 표식) ──
  // 이 게스트 계정의 데이터가 실계정으로 이전(migrateGuestData)되며 reset 된 후
  // true 로 stamp 된다. 재접속 시 서버가 isMigrated 시그널로 변환해 안 닫히는
  // 차단 팝업을 띄운다. 타겟 측 audit 필드(migrationReceivedAt/migrationHistory)와
  // 의미가 다르다 — 이쪽은 "이 계정은 빠져나갔으니 사용 불가" 표식.
  migrated?: boolean;
  // ── web2 → web3 계정 마이그레이션 (소스 측, RB migration Phase 3) ──
  // web2 `migration.requestMigration` 호출 시 PENDING 으로 CAS, 브리지가
  // `finalizeMigrationSource(DONE)` 을 부르면 DONE 으로 전이(+ `migrated=true`).
  // 게스트 마이그레이션의 `migrated`(boolean, 즉시 소진)와 달리 진행중 상태를
  // 클라 폴링(`migration.getMigrationStatus`)이 읽어야 해서 splash 노출 필드다
  // (splash-keys.ts 참조). 클라 지정 불가 — targetAddress 는 요청 시점 서버가 고정.
  migrationState?: {
    state: MigrationSourceState;
    targetAddress: string;
    migrationId: string;
    at: number;
  };
  // ── web2 → web3 계정 마이그레이션 (대상 측, RB migration Phase 2) ──
  // web3 `migrationTarget.applyInbound` 가 성공(applied) 시 stamp — 이 계정이
  // web2 의 어느 주소(P)로부터 데이터를 받았는지 audit 용. 클라 미노출 splash 키
  // 목록엔 없어도 되지만(신규 필드라 아직 분류 안 됨) 존재 자체는 감사 목적이라
  // server-only 로 분류한다(splash-keys.ts SERVER_ONLY_STATE_KEYS 참조).
  migratedFrom?: string;
  migrationAppliedAt?: number;
  // ── 결제 흔적 (룰1 시그널) ──
  // 첫 결제 시각 (epoch ms). $onItemPurchased 진입 시 1회 stamp. 이후 결제는 noop.
  // 마이그레이션 hard-block 룰1 의 단일 시그널 — backward-compat 으로 adRemovalPurchased / passes 보조.
  firstPurchasedAt?: number;
  // ── 구매 영수증 (지급 완료 확인용 링 버퍼) ──
  // $onItemPurchased 가 모든 지급 절차를 마친 뒤 append (최근 RECENT_PURCHASES_MAX 건).
  // 클라 purchase-confirm 폴링이 "스냅샷에 없던 새 purchaseId + productId 일치" 로
  // 지급 완료를 판정한다. 영수증 존재 = 해당 구매의 지급 절차 완료 보장.
  recentPurchases?: PurchaseReceiptEntry[];
  // ── Mail (MR-B: 컬렉션 → user state 이전) ──
  // 우편함 (사용자별). 서버가 sendMail 로 push, 클라는 listMyMail 로 조회·
  // claimMail 로 소비. inbox = claimedAt==null && expiresAt>now, history = claimedAt!=null.
  mail?: import('../mail/types').MailRow[];
  // ── League reward claims (MR-B: 컬렉션 → user state 이전) ──
  // 리그 시즌 보상 청구 문서 (사용자별). worker 가 snapshotLeagueRewards 로
  // 사용자별 state 에 직접 push (cross-user write). claim 시 claimedAt 갱신.
  leagueRewardClaims?: import('../master-data/rpc.types').LeagueRewardClaimDoc[];
  // ── Audit logs / history / seed issuance ──
  // RB-1552: suspiciousLogs · stage/league/valhalla/degen history · seedIssuance ·
  // currencyTransactions 는 전부 V8 collection 으로 이전됨 (account 필드로 소유자 식별,
  // cap 없이 보존 — 링버퍼 cap 유실 RB-1550 해소). user state 에서 제거.
  // suspiciousActivityCount(스칼라)만 user state 유지.
  // ── Tutorial 완료 셋 (RB tutorial server-state) ──
  // 본 유저가 완료한 튜토리얼 ID 집합(순서 무의미, dedupe). 클라 트리거 판정의
  // 단일 소스 — lastClearedStageId 와 같은 객체라 split-brain(재발동) 없음.
  completedTutorials?: string[];
  // ── Tutorial 스텝 진행도 (퍼널 분석용) ──
  // 튜토리얼 ID → 그 튜토리얼에서 가장 멀리 도달한 스텝 id. 완료하지 않고 이탈한
  // 유저도 "어느 말풍선에서 멈췄는지" 남는다. 최대값 판정은 스텝 순서를 아는
  // 클라이언트가 하고(서버는 순서를 모른다), 서버는 받은 값으로 덮어쓴다.
  tutorialProgress?: Record<string, string>;
}

/** 게스트 마이그레이션 수신 audit entry (UserGlobalState.migrationHistory). */
export interface MigrationHistoryEntry {
  fromAccount: string;
  receivedAt: number;
}

/** `currencyTransactions` collection 에 저장되는 통화 트랜잭션 doc (durable=BQ) — RB-1552. */
export interface CurrencyTransactionEntry {
  type: 'mint' | 'burn';
  assetId: string;
  amount: number;
  reason: string;
  timestamp: number;
  /** 광고 등 컨텍스트 직렬화 문자열(자유형식). reader 없음 — 감사/조회용. */
  memo?: string;
}

/** MR-D: suspicious_logs V8 collection 에 저장되는 의심 활동 로그 (RB-1552). */
export interface SuspiciousLogEntry {
  sessionId?: string;
  type: string;
  details: Record<string, unknown>;
  timestamp: number;
}

/** 구 suspicious 로그 user-state ring buffer 최대 건수 (레거시 cap — collection 은 무제한). */
export const AUDIT_LOG_MAX = 50;

/**
 * 구매 영수증 (`UserGlobalState.recentPurchases` 링 버퍼 1건).
 * $onItemPurchased 가 모든 지급 절차 완료 후 append — 존재 = 지급 완료.
 */
export interface PurchaseReceiptEntry {
  /** V8 플랫폼 발급 고유 구매 ID. 클라 onClose 페이로드에는 없음 — 서버 콜백 전용. */
  purchaseId: number;
  productId: string;
  /** 지급 절차 완료 시각 (서버 클럭 epoch ms). */
  deliveredAt: number;
}

/** recentPurchases 링 버퍼 최대 건수 — 폴링 확인 용도라 작게 유지. */
export const RECENT_PURCHASES_MAX = 10;

/**
 * 스킨 강화 멱등키 처리 결과 (`UserGlobalState.skinUpgradeIdemLog` 링 버퍼 1건).
 * `upgradeSkin` 이 burn·상태 커밋을 마친 뒤 append — 같은 `key` 재호출 시
 * burn 을 건너뛰고 이 값을 그대로 반환한다.
 */
export interface SkinUpgradeIdemEntry {
  key: string;
  /**
   * 페이로드 지문(리뷰 F2) — 같은 `key` 로 재호출됐을 때 요청이 원본과 동일한지
   * 대조하기 위함. 해시가 아니라 원본 4필드 그대로 저장한다(디버깅 시 어느
   * 필드가 다른지 바로 보인다). 불일치면 `upgradeSkin` 이 무관한 캐시값을
   * 조용히 반환하지 않고 throw 한다.
   */
  characterId: number;
  skinId: number;
  fromLevel: number;
  toLevel: number;
  level: number;
  spent: { gold: number; shard: number };
  recordedAt: number;
}

/** skinUpgradeIdemLog 링 버퍼 최대 건수 — 이중 클릭·재시도 흡수 용도라 작게 유지. */
export const SKIN_UPGRADE_IDEM_MAX = 10;

/**
 * 구 history user-state ring buffer 최대 보관 건수 (레거시 cap — collection 은
 * 무제한 보존, RB-1552). 테스트에서 "former HISTORY_MAX" 경계값으로 참조.
 */
export const HISTORY_MAX = 50;

/**
 * 구 seed issuance user-state ring buffer 최대 보관 건수 (레거시 cap — collection
 * 은 무제한 보존, RB-1552). 테스트에서 "former SEED_ISSUANCE_MAX" 경계값으로 참조.
 */
export const SEED_ISSUANCE_MAX = 30;

export type GameMode = 'STAGE' | 'LEAGUE' | 'VALHALLA' | 'DEGEN_LEAGUE' | 'FOLKVANGR';

export interface WaveEnemyInfo {
  id: number;
  name: string;
  resourceId: string;
  health: number;
  speed: number;
  atk: number;
  spawnInterval: number;
  score: number;
  /**
   * Carried through from `EnemyData.attackPatterns` so WaveManager / Enemy
   * can instantiate strategies at spawn time.
   */
  attackPatterns?: import('../master-data/types/enemy-attack.types').AttackPatternConfig[];
  /**
   * Side / timing / pulse overrides for this group (F4). Undefined keeps
   * the pre-F4 random-perimeter-by-spawnInterval behavior.
   */
  spawnPattern?: import('../master-data/types/spawn-pattern.types').SpawnPatternConfig;
  /**
   * Movement behavior for this group (F2). Undefined keeps
   * `moveTowardsPlayer()` straight-line behavior.
   */
  movementPattern?: import('../master-data/types/enemy-movement.types').MovementPatternConfig;
  /** Compound mob type identifier (e.g. 'F2', 'F7', 'C1'). Undefined for
   *  non-compound enemies. Set in the enemy data sheet and passed through by
   *  the wave pipeline to WaveManager for visual differentiation. */
  compoundType?: string;
  /** `EnemyData.slowResistance` 를 그대로 전달. 미지정 시 스폰에서 기본값 0. */
  slowResistance?: number;
  /** `EnemyData.knockbackResistance` 를 그대로 전달. 미지정 시 스폰에서 기본값 1. */
  knockbackResistance?: number;
}

export interface WaveInfo {
  waveNumber: number;
  killGoal: number;
  timeoutSeconds: number;
  silverReward: number;
  shopConfigId: number | null;
  itemCount: number;
  consumableItemCount: number;
  bg?: string;
  enemies: WaveEnemyInfo[];
  /** Max enemies alive simultaneously (F4). Undefined = no cap. */
  maxAliveEnemies?: number;
}

export interface WaveShopItem {
  id: number;
  grade: number;
  price: number;
}

export interface HitLogEntry {
  timestamp: number;
  damage: number;
  isDead: boolean;
}

export interface EnemyKillRecord {
  enemyId: number;
  normalKills: number;
  goldenKills: number;
}

export interface EnemySoulRecord {
  enemyId: number;
  normalSouls: number;
  goldenSouls: number;
}

export interface WaveRecord {
  waveNumber: number;
  enemyKills: EnemyKillRecord[];
  enemySouls: EnemySoulRecord[];
  consumablesUsed: { itemId: string; quantity: number }[];
  clearTimeMs: number;
  /**
   * SP5: 웨이브 클리어 시점 플레이어 HP — DEGEN HP_RATIO_CLEAR 서버 재판정용.
   * 클라 트래커 WaveClearSnapshot.hp(=character.getCurrentHealth()) 와 동일 소스.
   * codec 미반영(optional) — DEGEN 조건부 검증 경로에서만 소비.
   */
  clearHp?: number;
  /**
   * SP5: 웨이브 클리어 시점 최대 HP(디버프 룬 반영) — HP_RATIO 분모.
   * 클라 트래커 WaveClearSnapshot.maxHp(=getStats().getTotalStats()[HP]) 와 동일 소스.
   */
  clearMaxHp?: number;
}

export interface InitialSetupSnapshot {
  characterId: number;
  skinId: number | null;
  petIds: number[];
  petLevels: Record<number, number>;
  upgradeLevels: { upgradeId: number; level: number }[];
  appliedDifficultyIds: string[];
  /**
   * WAVESHOP_SILVER_MULT entitlement 배율(STAGE 전용). getSeed 시점의 활성
   * entitlement 로 서버가 계산해 attest 한다(seedRecord → 여기로 전파). 워커가
   * 이 값으로 wave silver 를 재계산해 클라 silver 와 일치시킨다. 다만 현재 live
   * 검증 경로(validateClear Step1.6 / validateAllWaveShops)는 wave-silver-income 을
   * 이 attested silverReward 와 대조하지 않으므로, 이 필드는 forward-looking
   * attestation 이다(RB-1172 replay runner / 향후 silver-income 검증이 소비할 때
   * load-bearing). 미보유/구엔트리(backfill)는 absent → 워커가 1 로 폴백.
   */
  silverMult?: number;
  /**
   * §B 마스터리 젠 배수 — STAGE 전용. getSeed 시점 검증 완료 difficultySelections 의
   * SELECTABLE 점수 배수 곱으로 판정한 구간 배수를 attest(seedRecord → 여기로 전파).
   * 워커가 이 값으로 동일 스폰 스케줄을 재현한다. tiers 부재/OFF/구엔트리(backfill)는
   * absent → 워커가 1 로 폴백.
   */
  masterySpawnMult?: number;
  /**
   * Task 11 (pet-breakthrough v6) — petId → 현재 돌파 단계. Task 11 픽스 라운드 1(TOCTOU)
   * 이후 petLevels/upgradeLevels 와 동일하게 **getSeed 시점에 freeze** 된 값이다
   * (`game.handler.ts` getSeed → seedRecord → `buildHistoryEntry`가 live userState 대신
   * 이 값을 history 로 전파). 워커가 `computeBreakthroughRunPlan`을 클라와 동일한 입력으로
   * 재계산해 tagBias(웨이브샵 편향)/synergyVirtual(가상 시너지 스택)/perPet(1돌 HP amp)
   * 파리티를 맞추는 데 쓴다. 구 레코드(freeze 이전 backfill)만 absent → live 폴백.
   */
  petBreakthroughLevels?: Record<number, number>;
  /**
   * Task 7(skin-enhance v3) — 장착 스킨 강화 레벨. petLevels/upgradeLevels 와 동일하게
   * **getSeed 시점에 freeze** 된 값이다(`game.handler.ts`/`degen-league.handler.ts`
   * getSeed → seedRecord → `buildHistoryEntry`/`buildAndComputeServerScore` 가 live
   * userState 대신 이 값을 읽는다). 그 사이 강화하면 제출 시점 상태가 발급 시점과
   * 달라져 워커가 정상 빌드를 치팅으로 오탐한다(pet-breakthrough v6 TOCTOU 와 동일
   * 함정). 서버 권위 점수의 `resolveSkinHp(character, skinId, skinLevel, statCoeff)`
   * 가 이 값을 소비한다(`statCoeff` 는 frozen 하지 않고 live gameConfig 에서 읽음
   * — collectionConfig 와 동일 관행).
   * 미장착(`skinId==null`) = null, 보유 중 레벨 미설정(구유저)·미지정(구 seed) = v3
   * 기본값 L1 과 동일 취급(하위호환). 디젠리그는 강화분을 격리하기 위해 항상 1 고정.
   */
  skinLevel?: number | null;
}

export interface RankingBuildSnapshot {
  characterId: number;
  skinId: number | null;
  /** breakthrough: 현재 돌파 단계(getSeed freeze petBreakthroughLevels 유래, 미돌파/구엔트리=0). */
  pets: { petId: number; level: number; breakthrough: number }[];
  upgradeLevels: { upgradeId: number; level: number }[];
  difficulties: DifficultySelection[];
  repeatPlayCount: number;
  /**
   * 이 베스트 런에서 도달한 웨이브 수. build 스냅샷과 동형으로 best 갱신 시에만 교체된다.
   * optional: 백필 안 함 → 구엔트리는 absent(표시 숨김).
   */
  waveReached?: number;
  /**
   * 이 런의 장착 스킨 강화 레벨 — `InitialSetupSnapshot.skinLevel`(getSeed freeze)을 그대로
   * 옮긴 값이다. 미장착 = null, 미강화 = 0. optional: 백필 안 함 → 구엔트리는 absent(표시 숨김).
   */
  skinLevel?: number | null;
  /**
   * 이 런의 컬렉션 포인트 — `SeedIssuanceEntry.collectionSnapshot`(getSeed freeze,
   * **계정 전역 도감** 기준이며 장착 로드아웃과 무관)을 그대로 옮긴 값이다. 등급 문자
   * (C~SSS)는 담지 않는다 — 랭크 사다리는 마스터데이터(`collectionConfig.ranks`)라
   * 표시 측이 `highestRank(ranks, pcp, scp)` 로 파생한다(도감 헤더와 동일 경로).
   * collectionConfig 미시딩 시 서버가 스탬프를 생략하므로 absent 가 될 수 있고,
   * 백필도 안 한다 → 구엔트리/미시딩 모두 absent(표시 숨김).
   */
  collection?: { pcp: number; scp: number };
}

export interface WaveShopHistoryRecord {
  /** 이 샵 레코드가 속한 웨이브 번호(1-indexed). */
  waveNumber: number;
  rerollCount: number;
  adRewardsReceived: number;
  /** 구매: 어느 reroll view(=구매 시점 rerollCount)의 어떤 itemId. price 는 서버가 시드 유도. */
  purchasedItems: { rerollView: number; itemId: number }[];
}

export interface WaveHistoryRecord {
  waveNumber: number;
  kills: EnemyKillRecord[];
  souls: EnemySoulRecord[];
  clearTimeMs: number;
  consumablesUsed: { itemId: string; quantity: number }[];
  shop?: WaveShopHistoryRecord;
}

export interface GameHistoryBase {
  account: string;
  sessionId: string;
  gameMode: GameMode;
  initialSetup: InitialSetupSnapshot;
  waves: WaveHistoryRecord[];
  score: number;
  scoreDetail: ScoreDetail;
  /**
   * RB-1540: server-stamped 결과 라벨. STAGE validator gate 가
   * `phase !== 'gameClear'` 인 record 를 skip 하는 데 사용 — cheat surface 0
   * (server authoritative), storage 미미, derive 룰(`waves[last].kills sum >=
   * killGoal && finalHp > 0`) 추가 비용 대비 이득 작아 유지.
   */
  phase: 'gameClear' | 'gameOver';
  seed: number;
  createdAt: number;
  completedAt: number;
  // Worker validation flag. Set to false on insert, flipped to true once the
  // gameplay-validation worker has processed the record (clean or violating).
  // `undefined` is treated as "not validated" by the scheduler for backfill.
  validated?: boolean;
  /** 워커 검증 완료 시각(ms). markRecordValidated 가 validated=true 와 함께 기록. */
  validatedAt?: number;
  /** 워커가 발견한 위반 코드 목록(어드민 현황 표시용). 위반 없으면 빈 배열/미설정. */
  workerViolationCodes?: string[];
  /** 제출 시점 플레이어 HP — 리플레이로 점수/HP 재검증용. Optional(backfill). */
  finalHp?: number;
  /**
   * 검증 관련 client 컬렉션의 {version, hash} 핀. 워커가 version 으로 R2 영구 파일을
   * 직접 fetch(재시드 무관). 단일 `masterDataManifestHash` 를 대체. Optional(backfill —
   * 구엔트리는 없음 → 워커는 최신 master data 로 폴백).
   */
  sheetPins?: import('../master-data/sheet-pins').SheetPins;
  /**
   * RB-1167: true when this submission set a new personal best for the
   * mode/modeId. Server-stamped at submitResult. Worker gates LEAGUE replay
   * on this field (`shouldRunReplay` requires it). Optional for backfill —
   * pre-RB-1167 rows lack it; gate treats `undefined` as `false`.
   */
  isPersonalBest?: boolean;
  /**
   * RB-1167: Rank within the league ranking collection immediately after
   * the score insert (1-indexed; ties collapse to the same rank since the
   * count uses `$gt`). `null` for STAGE / VALHALLA. Optional for backfill.
   */
  rankAtSubmit?: number | null;
}

export interface StageHistoryEntry extends GameHistoryBase {
  gameMode: 'STAGE';
  stageId: number;
  stageDocId: string;
  reviveCount: number;
  deliveredRewards?: Record<string, number>;
  /**
   * @deprecated legacy mirror. The new authoritative counter is `bonusClaimCount`.
   * For external-reader compatibility `claimAdStageReward` still sets this true
   * whenever the claim count is >= 1.
   */
  adRewardClaimed?: boolean;
  /**
   * Web3 TX reward §4.2: number of stacked stage bonus claims. Incremented on
   * each `claimAdStageReward` call. tx/adClaimCount 의 합. source별 cap 은
   * stageBonusConfig 참조. Treated as 0 when undefined.
   */
  bonusClaimCount?: number;
  /**
   * web3 TX 추가 지급 누적 횟수. 미정의 시 0으로 해석.
   * bonusClaimCount = txClaimCount + adClaimCount 를 유지.
   */
  txClaimCount?: number;
  /**
   * 광고 추가 지급 누적 횟수. 미정의 시 레거시 bonusClaimCount(또는 adRewardClaimed)로 해석.
   */
  adClaimCount?: number;
}

export interface LeagueHistoryEntry extends GameHistoryBase {
  gameMode: 'LEAGUE';
  /** Identifier of the LeagueSeason this submission belongs to. Was named
   *  `leagueId` pre league-season-table-split; renamed to disambiguate from
   *  `LeagueTableData.id` (combat config). */
  seasonId: number;
  leagueDocId: string;
  leaguePlayCount: number;
}

export interface ValhallaHistoryEntry extends GameHistoryBase {
  gameMode: 'VALHALLA';
  valhallaId: number;
  valhallaDocId: string;
  highestWave: number;
  deliveredRewards?: Record<string, number>;
}

/**
 * Degen League run history stored in the DEGEN_LEAGUE_HISTORY V8 collection
 * (RB-1552). Mirrors the `DegenHistoryEntry` shape recorded by
 * `degen-league.handler.ts` submitResult. The degen runtime types
 * (`BlessingRune`/`ConditionalBlessing`) are imported lazily to keep the
 * user-state module's eager imports minimal.
 */
export interface DegenLeagueHistoryEntry {
  account: string;
  sessionId: string;
  /** 발급 seed(seed_issuance.seed). 워커 SEED_VALUE_MISMATCH cross-check 대조값. */
  seed: number;
  seasonId: number;
  difficulty: import('../degen').DegenDifficulty;
  runes: import('../degen').BlessingRune[];
  conditionals: import('../degen').ConditionalBlessing[];
  achievedConditionalIds: string[];
  normalizationFactor: number;
  baseScore: number;
  statMultiplier: number;
  conditionalBonus: number;
  bustWave: 1 | 2 | null;
  bustFactor: number;
  finalScore: number;
  playLog: WaveRecord[];
  validated: boolean;
  createdAt: number;
  /** 검증 관련 client 컬렉션 핀(워커 fetch). 단일 manifest 해시 대체. */
  sheetPins?: import('../master-data/sheet-pins').SheetPins;
}

/**
 * Game seed issuance record stored in the SEED_ISSUANCE V8 collection (RB-1552).
 *
 * Mirrors the `SeedIssuanceRecord` shape written into the SEED_ISSUANCE
 * collection by `game.handler.ts` getSeed and `degen-league.handler.ts`
 * getSeed. The collection's auto-generated `__id` becomes `sessionId`, so it
 * is captured explicitly here. Degen-specific draw fields are intentionally
 * NOT mirrored — seed-reuse validation only needs identity + timing.
 */
export interface SeedIssuanceEntry {
  sessionId: string;
  account: string;
  seed: number;
  gameMode: GameMode;
  modeId: number;
  characterId: number;
  skinId: number | null;
  equippedPetIds: number[];
  /** 신모델: 유저가 getSeed 시 선택한 난이도 목록 (effectId+level 쌍). 서버가 submitResult 시 재도출. */
  difficultySelections: DifficultySelection[];
  issuedAt: number;
  used: boolean;
  usedAt?: number;
  leaguePlayCountAtIssuance?: number;
  /**
   * WAVESHOP_SILVER_MULT — STAGE 전용 entitlement 배율을 getSeed 시점에 attest.
   * getSeed 응답으로도 같은 값을 돌려줘 클라가 in-run silver 계산에 single-source 로
   * 쓰고, submitResult 의 buildHistoryEntry 가 initialSetup.silverMult 로 전파한다.
   * 현재 live 검증엔 wave-silver-income 소비자가 없어 forward-looking attestation —
   * RB-1172 replay / 향후 silver-income 검증에서 load-bearing 이 됨.
   * STAGE 외/미보유/구엔트리는 undefined(=1).
   */
  silverMult?: number;
  /**
   * §B 마스터리 젠 배수 — STAGE 전용. getSeed 시점 검증 완료 difficultySelections 의
   * SELECTABLE 점수 배수 곱으로 판정한 구간 배수를 attest. getSeed 응답으로도 같은
   * 값을 돌려줘 클라가 wave-info 젠 속도 계산에 single-source 로 쓰고, submitResult 의
   * buildHistoryEntry 가 initialSetup.masterySpawnMult 로 전파한다. tiers 부재/OFF/
   * STAGE 외/구엔트리는 undefined(=1).
   */
  masterySpawnMult?: number;
  /**
   * TOCTOU freeze (P7): getSeed 시점의 장착 로드아웃 레벨. submitResult 의
   * buildHistoryEntry 가 live userState 대신 이 값을 history.initialSetup 으로 전파해
   * 워커가 게임 시작 시점 전투력으로 DPS 를 재구성한다. getSeed↔submit 사이 레벨업으로
   * 부풀린 값이 새는 걸 막는다. 구 엔트리(없음)는 buildHistoryEntry 가 live 폴백.
   */
  petLevels?: Record<number, number>;
  upgradeLevels?: { upgradeId: number; level: number }[];
  /**
   * Task 11 픽스 라운드 1 — pet-breakthrough v6 TOCTOU freeze. getSeed 시점의 장착 펫
   * 돌파 단계(petId → breakthrough). submitResult 의 buildHistoryEntry/즉시검증/점수
   * 권위 재구성이 live userState 대신 이 값을 읽어야 getSeed↔submit 사이 돌파(합성)로
   * 인한 tagBias/synergyVirtual/perPetAmp drift(WAVE_SHOP_PURCHASE_NOT_OFFERED 오탐
   * 등)를 막는다. 구 엔트리(없음)는 buildHistoryEntry/즉시검증이 live 폴백.
   */
  petBreakthroughLevels?: Record<number, number>;
  /**
   * Task 7(skin-enhance v3) — petLevels/upgradeLevels 와 같은 트랜잭션에서 freeze 하는
   * 장착 스킨 강화 레벨. submitResult 의 buildHistoryEntry/buildAndComputeServerScore
   * 가 live userState 대신 이 값을 읽어야 getSeed↔submit 사이 강화로 인한 오탐을
   * 막는다(펫 돌파 v6 TOCTOU 와 동일 패턴). 미장착(skinId==null) = null, 보유 중
   * level 미설정(구유저)·구 엔트리(필드 없음)는 소비처가 v3 기본값 L1 취급(하위호환).
   * 디젠리그(`DegenSeedRecord`)는 EV 격리를 위해 항상 1 고정.
   */
  skinLevel?: number | null;
  /**
   * Task F5 활성화(getSeed stamp, 레인 A 마무리 조각) — 컬렉션 v9 2트랙 포인트(pcp/scp)
   * 스냅샷. getSeed 시점의 **계정 전역 도감**(장착 로드아웃이 아니라 보유 펫/스킨 전체)
   * 상태에서 `computeCollectionPoints` 로 계산해 freeze 한다. petLevels/petBreakthroughLevels
   * 와 동일 TOCTOU-freeze 절충(P7) — 클라 계산 시점(Game 부팅)과 getSeed 시점 사이의 근소한
   * 차이(그 사이 펫/스킨 상태 변경)는 감수한다.
   *
   * **frozen-only(라이브 폴백 없음)**: 워커 score-forensics 는 계정 전역 도감 상태를
   * 재현할 수 없어(history 에 없음, 계정 상태 자체가 없음) live 재계산이 불가능하다.
   * 서버가 live 로 반영하면 워커 재계산 maxHp/DPS 가 어긋나 SCORE_RECOMPUTE_DELTA(W2)가
   * 정상 고컬렉션 유저를 오탐한다 — services/server/src/utils/authoritative-score.ts
   * `AuthoritativeScoreSeed.collectionSnapshot` 주석 참고. 서버 점수 경로·워커
   * gameplay-validator 모두 이 seed 레코드 필드를 **직접** 읽는다(history 미경유).
   *
   * 구 레코드(필드 부재)/collectionConfig 미시딩(getSeed 가 fail-closed 로 스탬프 생략) =
   * undefined → 소비처 전부 컬렉션 미반영(byte-identical, 기존 동작 보존).
   */
  collectionSnapshot?: { pcp: number; scp: number };
  validated?: boolean;
  /**
   * STAGE 에서 getSeed 가 실제 burn 한 stage_ticket 수량. 실패/포기 환불이 이 값을
   * 그대로 mint 해 burn↔refund 대칭을 보장(세션 중 마스터데이터 변경에도 무관).
   * 구 엔트리(없음)는 refund 경로가 ?? 1 폴백. STAGE 외엔 undefined.
   */
  burnedTickets?: number;
  /**
   * 오버드라이브 배팅 티켓 수. 미배팅/비-STAGE/구엔트리 undefined. 배팅분은
   * 시드 발급 시 확정 소모 — burnedTickets(기본분)와 분리 기록해 stage_fail_refund/
   * refundStageSeed 가 무변경으로 기본분만 환불한다(배팅 비환급 by design).
   */
  overdriveTickets?: number;
  /**
   * getSeed 시점 config attest — 오버드라이브 점수 배수. submitResult 의 서버 권위
   * 점수 곱과 워커 포렌식 재계산이 이 값만 신뢰한다(클라 입력은 배팅 수량뿐).
   * 미배팅/비-STAGE/구엔트리 undefined(=1).
   */
  overdriveScoreMult?: number;
  /**
   * 복원 프롬프트 "포기"(game.refundStageSeed)로 stage_ticket을 환불한 시각(ms).
   * 환불 시 used=true와 함께 set — used 로 이중취득/중복환불을 막고, refundedAt 으로
   * "제출돼서 used"와 "환불돼서 used"를 분석/디버깅 시 구분한다. 비환불은 undefined.
   */
  refundedAt?: number;
  /**
   * MR-C (Phase 3): DEGEN_LEAGUE 전용 draw 스냅샷. 컬렉션 제거 후 degen
   * submitResult 가 서버 측 재채점에 필요한 룬/조건부/정규화/시즌 정보를 user
   * state seed mirror 에서 읽어야 하므로 여기에 함께 적재한다. 비-degen 모드는
   * 모두 undefined. seed-reuse/timing 검증은 이 필드를 보지 않는다.
   */
  degenDifficulty?: import('../degen').DegenDifficulty;
  degenRunes?: import('../degen').BlessingRune[];
  degenConditionals?: import('../degen').ConditionalBlessing[];
  degenNormalizationFactor?: number;
  degenSeasonId?: number;
  /**
   * 서버 전용 조건부 룰렛 roll seed. getSeed 시점에 생성·적재하되 getSeed DTO
   * 로는 절대 반환하지 않는다(클라가 roll 을 사전 예측·리롤하는 메타게이밍 차단).
   * submitResult 의 잭팟 룰렛 파생에만 쓰인다. 구 in-flight 엔트리는 undefined.
   */
  degenCondSeed?: number;
  /**
   * DEGEN_LEAGUE 벌크 베팅 — 이 판에 소모(burn)한 디젠 티켓 수. getSeed 가 적재하고
   * submitResult 가 점수·글로리를 ×N 하는 데 쓴다. 비-degen·구엔트리는 undefined(=1).
   */
  degenTicketCount?: number;
  /**
   * DEGEN_LEAGUE 입장에 쓴 티켓 종류. 실버 티켓 런은 `'silver'`, 골든 티켓 런은
   * `'golden'` 으로 스탬프되고, submitResult 가 이 값만 보고 재원 계통 제외(무상
   * 티켓 = 실버·골든: 잭팟 적립·NCG 상금풀·구제 손실 카운터) 또는 배율 하한
   * clamp(골든) 를 판정한다(클라 주장값 불신 — 이 스탬프만 신뢰). 랭킹 계통은
   * 종류와 무관하게 기록된다 — 기획서 §8.5.
   * 기존 티켓·구엔트리는 undefined(= normal).
   */
  degenTicketKind?: 'silver' | 'golden';
  /**
   * Task 7 — 골든 런(구제 지급 전용) 진입 시 getSeed 가 적용한 배율 하한
   * (`rescue.golden.floorMultiplier` 스냅샷). degenTicketKind가 `'golden'`일 때만
   * 존재. submitResult 의 점수 계산이 이 값으로 clamp 해 실제 적용 하한을 재현한다.
   */
  degenGoldenFloor?: number;
  /**
   * Task 7 — 골든 재추첨(`drawGoldenBlessings`) 이 하한 이상 룬 세트를 얻기까지
   * 시도한 횟수(상한 도달 시 마지막 시도 채택). 포렌식/모니터링 전용 — 점수 계산에는
   * 쓰이지 않는다. degenTicketKind가 `'golden'`일 때만 존재.
   */
  degenGoldenAttempts?: number;
  /**
   * Task 7 — 재추첨이 `maxRedraws` 를 소진해 하한 미달 상태로 마지막 추첨을 채택했는지
   * 여부. true 면 점수 계산의 clamp 가 실제 배율을 끌어올렸다는 뜻(포렌식 표시용).
   * degenTicketKind가 `'golden'`일 때만 존재.
   */
  degenGoldenClamped?: boolean;
  /**
   * 디젠 S2 누적상금 판정 전용 서버 시드. getSeed 가 generateSeed() 로 발급·적재하며
   * getSeed DTO 로는 절대 반환하지 않는다(클라 사전 예측·리롤 차단 — condSeed 와 동일 계약).
   * **폴백 금지**: 부재(구 in-flight 시드) 시 submitResult 는 미니/메가 판정 자체를 skip
   * 한다 — `?? seed` 류 폴백은 seed 가 클라 공개값이라 상금 경로에 절대 넣지 않는다.
   */
  degenPrizeSeed?: number;
}

/**
 * Per-tier Valhalla state derived locally from master data + user state.
 *
 * 해금은 **활성 시즌 세트 내 ordinal(정렬 위치)** 기준이라 tier id 가 비연속이어도
 * 동작한다. `valhalla.id` 는 전역 PK 라 시즌별 세트(다른 tableId)는 id 가 1..N 으로
 * 연속하지 않을 수 있다. `computeValhallaTierStates` 가 ordinal 을 계산해 반환한다.
 * `cleared = ordinal <= clearedOrdinal`, `unlocked = ordinal <= clearedOrdinal + 1`.
 * 첫 티어(가장 낮은 id, ordinal=1)는 항상 unlocked. `highestWave` 는 해당 tier 최고도달 웨이브.
 */
export interface ValhallaTierState {
  tierId: number;
  unlocked: boolean;
  cleared: boolean;
  highestWave: number;
}

export interface ScoreDetail {
  killScore: number;
  goldenSoulScore: number;
  /** RB-1847: rawScore × hp × time × synergy (마스터리 무관 성능 성분). preMastery 계산 소스. */
  baseScore: number;
  hpMultiplier: number;
  timeMultiplier: number;
  synergyMultiplier: number;
  clearedWaveCountMultiplier: number;
  difficultyMultiplier: number;
  additionalMultiplier: number;
  totalMultiplier: number;
  finalScore: number;
  // Display metadata for result screen
  totalKillGoal: number;
  totalActualKills: number;
  survivalRate: number;
  avgClearSpeedRatio: number;
  achievedSynergyTiers: number;
  maxSynergyTiers: number;
  /** 오버드라이브 점수 배수(서버 권위, 시드 attest). 미배팅 런은 undefined(결과 화면 행 비표시). */
  overdriveMultiplier?: number;
}

export interface GameResult {
  score: number;
  scoreDetail: ScoreDetail;
  totalSilverEarned: number;
  totalPlayTime: number;
  clearedAt: number;
  canRevive?: boolean;
  reason?: string;
  deliveredRewards?: Record<string, number>;
}

/**
 * External-redemption asset guard (`glory`):
 *
 * Subject to swap with YGG Play's $YGG token. Isolated from the in-game
 * economy.
 *
 * DO NOT add `glory` to:
 *   - DELIVERY_ASSET_MAP / CONSUMABLE_ASSET_MAP / STATE_DELIVERY_MAP below
 *   - Master-data sheet reward / pass / quest columns
 *   - Admin grant tooling (debug-mode handler is the only exception)
 *
 * The external redemption backend (ygg-redeem) has been retired; `glory`
 * remains an in-game earning currency but no longer has an external mint/
 * redemption path.
 *
 * 예외 — **메일 채널 한 곳**만 열려 있다(디젠 시즌 보상 등 운영 수동 지급). 그 통로는
 * `MAIL_ONLY_DELIVERY_ASSET_MAP` 이며, `deliverItems` 는 호출부가
 * `{ allowMailOnlyAssets: true }` 로 명시 옵트인할 때만 이 맵을 본다. 옵트인은
 * `userMail.claimMail` 단 한 곳에서만 넘긴다 — 다른 지급 경로(퀘스트·패스·샵·리그·
 * 출석)는 GLORY 키를 여전히 해석하지 못한다. 새 호출부에서 이 옵션을 켜지 말 것.
 *
 * 소비(burn) 경로: 외부 리딤 외에 **글로리 샵**(`userGloryShop.purchase`)이 추가됐다.
 * 소비는 이 가드와 무관하다 — 금지 대상은 어디까지나 `glory` 를 **지급**하는 채널이다.
 * 글로리 샵 상품의 `delivery_items` 에 GLORY 가 들어오면 서버가 명시적으로 거부한다
 * (조용히 무시하면 운영 사고가 되므로).
 */
export type AssetId =
  | 'gold'
  | 'ruby'
  | 'emerald'
  | 'sapphire'
  | 'stage_ticket'
  | 'league_ticket'
  | 'golden_blessing_token'
  | 'valhalla_ticket'
  | 'degen_ticket'
  // 폴크방 총력전 입장권. 스테이지·리그·발할라 티켓과 달리 시간 리필이 없다 —
  // 지급 경로(초기 지급·퀘스트·패스·샵·메일)로만 들어오고 입장 시 1장 소모된다.
  | 'folkvangr_ticket'
  // 디젠 실버티켓 — 입장 소모는 degen_ticket 과 동일하되 어떤 랭킹에도 기록되지 않는다
  // (시즌랭킹·명예의전당·lifetime 누적·NCG 상금풀 전부 skip). 보상은 글로리만.
  | 'degen_silver_ticket'
  // 디젠 골든티켓 — 누적 순손실 임계 도달 시 지급. 입장하면 가호 배율에 하한이
  // 보장되고 bust 가 면제된다. 배팅 장수는 서버가 마스터데이터로 강제한다.
  | 'degen_golden_ticket'
  | 'upgrade_reset_ticket'
  | 'quick_gain_ticket'
  | 'thor_wrath'
  | 'blessing_of_freia'
  | 'glory'
  // 펫 돌파석 4종(v6) — 조각만 드랍, 상위는 1:10 합성 전용 재화
  | 'breakthrough_stone_piece'
  | 'breakthrough_stone_low'
  | 'breakthrough_stone_mid'
  | 'breakthrough_stone_high'
  // 스킨 강화 전용 재화. 등급별 차등은 소모량으로만 표현하므로 1종이다.
  // v1 획득처 = 중복 스킨 자동 변환(+메일·어드민 지급). 유료 판매 금지(기획 §5 리스크4).
  | 'skin_shard';

/**
 * Consumable inventory asset map.
 *
 * DO NOT add `glory` — it is not in-game consumable.
 * See AssetId guard above for rationale.
 */
export const CONSUMABLE_ASSET_MAP: Record<string, AssetId> = {
  THOR_WRATH: 'thor_wrath',
  BLESSING_OF_FREIA: 'blessing_of_freia',
  UPGRADE_RESET_TICKET: 'upgrade_reset_ticket',
  QUICK_GAIN_TICKET: 'quick_gain_ticket',
};

/**
 * Reward-delivery asset map.
 *
 * DO NOT add `glory` — external-redemption assets must never reach the
 * general reward channel.
 * See AssetId guard above for rationale.
 */
export const DELIVERY_ASSET_MAP: Record<string, AssetId> = {
  EMERALD: 'emerald',
  GOLD: 'gold',
  RUBY: 'ruby',
  SAPPHIRE: 'sapphire',
  STAGE_TICKET: 'stage_ticket',
  LEAGUE_TICKET: 'league_ticket',
  VALHALLA_TICKET: 'valhalla_ticket',
  DEGEN_TICKET: 'degen_ticket',
  DEGEN_SILVER_TICKET: 'degen_silver_ticket',
  DEGEN_GOLDEN_TICKET: 'degen_golden_ticket',
  FOLKVANGR_TICKET: 'folkvangr_ticket',
  UPGRADE_RESET_TICKET: 'upgrade_reset_ticket',
  QUICK_GAIN_TICKET: 'quick_gain_ticket',
  GOLDEN_BLESSING: 'golden_blessing_token',
  THOR_WRATH: 'thor_wrath',
  BLESSING_OF_FREIA: 'blessing_of_freia',
  BREAKTHROUGH_STONE_PIECE: 'breakthrough_stone_piece',
  BREAKTHROUGH_STONE_LOW: 'breakthrough_stone_low',
  BREAKTHROUGH_STONE_MID: 'breakthrough_stone_mid',
  BREAKTHROUGH_STONE_HIGH: 'breakthrough_stone_high',
  SKIN_SHARD: 'skin_shard',
};

/**
 * 메일 채널 전용 delivery 자산 맵.
 *
 * `DELIVERY_ASSET_MAP` 과 의도적으로 분리돼 있다 — 저쪽은 퀘스트·패스·샵·리그·출석 등
 * 모든 지급 경로가 공유하므로 여기에 glory 를 넣으면 외부 리딤 자산이 게임 경제 전체로
 * 새어나간다. `deliverItems` 는 `{ allowMailOnlyAssets: true }` 옵트인이 있을 때만 이
 * 맵을 조회하고, 그 옵트인은 `userMail.claimMail` 만 넘긴다.
 *
 * 이 맵을 키우거나 새 호출부에서 옵트인하기 전에 위 AssetId 가드 주석을 먼저 읽을 것.
 */
export const MAIL_ONLY_DELIVERY_ASSET_MAP: Record<string, AssetId> = {
  GLORY: 'glory',
};

/** 무료 기본 장착 슬롯 수(1·2번). petSlotCount 미설정(legacy) 시 폴백 상한. */
export const FREE_PET_SLOTS = 2;

/**
 * 장착 펫 슬롯의 하드 상한(1..6). 무료 2 + 상점 2 + 별모으기 2 = 6.
 * 클라 `TOTAL_PET_SLOTS`(apps/client/src/constants/petSlot.ts)와 동일 값.
 *
 * 서버 anti-cheat 의 장착 개수 상한은 per-user `petSlotCount`(= 무료 + 상점 + 별퀘
 * 해금의 총합; PET_SLOT/PET_SLOT_QUEST 딜리버리가 항상 함께 증가시켜 실제 열린 슬롯
 * 수와 동일)이며, 이 상수는 그 위의 하드 상한(over-delivery 방어)으로만 쓴다.
 */
export const MAX_EQUIP_PET_SLOTS = 6;

/**
 * UserGlobalState field map (delivery → state).
 *
 * DO NOT add any `glory`-related key here.
 */
export const STATE_DELIVERY_MAP: Record<
  string,
  keyof UserGlobalState | Array<keyof UserGlobalState>
> = {
  PET_SLOT: 'petSlotCount',
  // 퀘스트 소스 태깅: 총 해금 수(petSlotCount)와 퀘스트 해금 수(questPetSlotCount)를
  // 동시에 올려 클라가 5·6번 위치에 배치하도록 한다.
  PET_SLOT_QUEST: ['petSlotCount', 'questPetSlotCount'],
};

// Legacy monolithic collection name kept only for one-off cleanup paths.
export const LEAGUE_RANKINGS_COLLECTION = 'leagueRankings';

export interface LeagueRankingEntry {
  account: string;
  nickname?: string;
  score: number;
  characterId: number;
  seasonId: number;
  playCount: number;
  timestamp: number;
  rankKey: string;
  /** RB-1715: 검증 상태. 미설정 = LIVE(legacy 호환). */
  validationState?: import('../clear-validation/validation-state').ValidationState;
  /** RB-1715: 상태 전이 시각 + 사유 + 멱등 dedup 키. */
  validationStateAt?: number;
  validationReason?: string;
  /** RB-1715: 멱등 전이용 — 동일 (signalId,toState) 재적용 방지. */
  lastValidationSignalId?: string;
  /** RB-1715: 비파괴 upsert — 직전 best 스냅샷(격리/복원·감사용). */
  prevBestScore?: number;
  prevBestRankKey?: string;
  prevBestTimestamp?: number;
  /** 리그 랭킹 빌드 공개(RB): 베스트 런의 빌드 스냅샷. 백필 안 함 → 구엔트리는 absent. */
  build?: RankingBuildSnapshot;
  /**
   * RB-1847: best 런의 preMasteryScore(= baseScore × clearedWaveCountMultiplier).
   * 마스터리 화면에서 현재 배율을 곱해 예상 점수 재구성용. 백필 안 함 → 구엔트리는 absent.
   */
  preMasteryScore?: number;
  /** RB: best 점수를 만든 플레이의 sessionId. history 추적 + 검증 타깃(bestValidated 짝). */
  bestSessionId?: string;
  /** RB: bestSessionId 플레이가 리플레이 보상검증을 통과했는지. validationState(안티치트)와 별개 축. */
  bestValidated?: boolean;
}

export interface UserStateWithAssets extends UserGlobalState {
  /**
   * Splash baseline asset map. **Do not drop from server response** —
   * `userAccount.init` / `userAccount.getMyState` 가 이 필드를 채워야 헤더가
   * V8 `useAsset` push 첫 emit 누락 케이스에서도 0 으로 표시되지 않는다.
   * (회귀 이력: 38d247a7 도입 → 67ddb448/75ac89f6 에서 제거되며 splash 재화
   * 0 회귀 → 본 fix 로 재도입.) 로컬 모드도 동일 필드 사용 (push 채널 없음).
   * Optional 인 이유는 호출 경로 일부(예: 비-splash 컨텍스트의 부분 응답)에서
   * 생략 가능하기 때문 — splash 응답은 [[SplashResponse]] 가 required 로 강제.
   */
  assets?: Record<string, number>;
  /** Server-provided guest flag (state 의 isGuest 미러) */
  isGuest?: boolean;
  /**
   * 서버 권위 UTC 날짜(YYYY-MM-DD). splash 응답에서 서버가 자기 클럭으로 계산해
   * 내려준다. 클라는 출석/펫프리뷰 "오늘" 판정에 기기 시계 대신 이 값을 쓴다.
   * 영속 state 가 아닌 파생 필드 — assets 와 동일 패턴(매 splash 갱신).
   */
  serverDateUtc?: string;
}

/**
 * `userAccount.init` / `userAccount.getMyState` 응답 정확형. SPLASH_STATE_KEYS
 * partial-fetch 결과 + 항상-포함 assets baseline. 서버 핸들러 반환형 / 클라
 * RPC 호출부가 공유. assets 를 누락시키면 splash 헤더가 0 으로 표시되므로
 * **required**.
 */
export type SplashResponse = Pick<UserGlobalState, SplashStateKey> & {
  assets: Record<string, number>;
  serverDateUtc: string;
  /**
   * 서버가 부팅 응답에 싣는 밴 사실(파생 boolean). raw `banned` 상태 키(SERVER_ONLY)
   * 와 의도적으로 다른 이름 — 사유·시각은 포함하지 않는다. 클라는 이 값이 true 면
   * 닫히지 않는 밴 팝업을 띄운다.
   */
  isBanned?: boolean;
  /**
   * 서버가 부팅 응답에 싣는 마이그레이션 사실(파생 boolean). raw `migrated` 상태 키
   * (SERVER_ONLY)와 의도적으로 다른 이름. 클라는 이 값이 true 면 닫히지 않는
   * 마이그레이션 차단 팝업을 띄우고 부팅을 중단한다.
   */
  isMigrated?: boolean;
};

/**
 * web3 신규 가입 종료 응답 — 미초기화 (지갑, 비게스트) 계정의 `userAccount.init` 이 web3 verse 에서 계정을
 * 만들지 않고 이 값만 돌려준다(state 필드 없음). 클라는 닫히지 않는 안내 팝업을 띄우고
 * 부팅을 중단한다.
 */
export type SignupClosedResponse = {
  assets: Record<string, number>;
  serverDateUtc: string;
  isSignupClosed: true;
};

export type { EntitlementType } from '../master-data/types/entitlement.types';
export type { UserEntitlementState } from '../entitlements';

export {
  SPLASH_STATE_KEYS,
  SERVER_ONLY_STATE_KEYS,
  type SplashStateKey,
  type ServerOnlyStateKey,
} from './splash-keys';
